// src/pages/settingsUI.jsx
// Shared building blocks for the Settings pages (Settings.jsx, PublicHoliday.jsx).
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Loader2, Minus, Plus, Search, X, AlertTriangle, Info } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
export const cx = (...classes) => classes.filter(Boolean).join(' ');

export function fullName(m = {}) {
  if (m.firstName || m.lastName) return `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
  return m.name ?? m.email ?? '';
}

export function getInitials(name = '') {
  return name.split(' ').filter(Boolean).map(p => p[0]).join('').toUpperCase().slice(0, 2) || '?';
}

const AVATAR_COLORS = ['#1C1A14', '#3A362B', '#4A4234', '#645B4A', '#6E4C00', '#8A5F00', '#5C4320', '#2E2A20'];
export function avatarColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// Pulls a list out of the various response shapes the API returns
export function unwrapList(res, ...keys) {
  if (Array.isArray(res)) return res;
  const d = res?.data ?? res;
  if (Array.isArray(d)) return d;
  for (const key of keys) {
    if (Array.isArray(d?.[key])) return d[key];
    if (Array.isArray(res?.[key])) return res[key];
  }
  return [];
}

export const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

// ─────────────────────────────────────────────────────────────
// Animations (render once per page)
// ─────────────────────────────────────────────────────────────
export function UIStyles() {
  return (
    <style>{`
      @keyframes ph-fade { from { opacity: 0 } to { opacity: 1 } }
      @keyframes ph-pop  { from { opacity: 0; transform: translateY(8px) scale(.98) } to { opacity: 1; transform: none } }
      @keyframes ph-rise { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
      .ph-fade { animation: ph-fade .18s ease-out both }
      .ph-pop  { animation: ph-pop .2s cubic-bezier(.2,.9,.3,1.2) both }
      .ph-rise { animation: ph-rise .25s ease-out both }
      .ph-num::-webkit-outer-spin-button, .ph-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0 }
      .ph-num { -moz-appearance: textfield }
      @media (prefers-reduced-motion: reduce) { .ph-fade, .ph-pop, .ph-rise { animation: none } }
    `}</style>
  );
}

// ─────────────────────────────────────────────────────────────
// Buttons
// ─────────────────────────────────────────────────────────────
const BUTTON_VARIANTS = {
  primary:   'bg-[#FFC81E] text-[#1C1A14] shadow-sm hover:bg-[#F5A300]',
  secondary: 'border border-sand-200 bg-[color:var(--surface)] text-sand-700 hover:border-sand-300 hover:bg-sand-50',
  ghost:     'text-sand-600 hover:bg-sand-100 hover:text-sand-900',
  danger:    'bg-red-600 text-white shadow-sm hover:bg-red-700',
  dangerGhost: 'text-[color:var(--bad-text)] hover:bg-[color:var(--bad-bg)] hover:text-[color:var(--bad-text)]',
};
const BUTTON_SIZES = {
  sm: 'h-8 gap-1.5 rounded-lg px-3 text-[13px]',
  md: 'h-10 gap-2 rounded-xl px-4 text-sm',
};

export function Button({ variant = 'secondary', size = 'md', loading = false, icon: Icon, children, className, disabled, ...props }) {
  const iconSize = size === 'sm' ? 13 : 15;
  return (
    <button
      type="button"
      {...props}
      disabled={disabled || loading}
      className={cx(
        'inline-flex shrink-0 items-center justify-center whitespace-nowrap font-medium transition-all active:scale-[0.98]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        BUTTON_SIZES[size], BUTTON_VARIANTS[variant], className,
      )}
    >
      {loading ? <Loader2 size={iconSize} className="animate-spin" /> : Icon ? <Icon size={iconSize} /> : null}
      {children}
    </button>
  );
}

export function IconButton({ label, tone = 'default', className, children, ...props }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...props}
      className={cx(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sand-500 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'danger' ? 'hover:bg-[color:var(--bad-bg)] hover:text-[color:var(--bad-text)]' : 'hover:bg-sand-100 hover:text-sand-900',
        className,
      )}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Small display pieces
// ─────────────────────────────────────────────────────────────
const CHIP_TONES = {
  sand:  'bg-sand-100 text-sand-700',
  ink:   'bg-ink-50 text-ink',
  green: 'bg-[color:var(--ok-bg)] text-[color:var(--ok-text)]',
  red:   'bg-[color:var(--bad-bg)] text-[color:var(--bad-text)]',
  amber: 'bg-[color:var(--warn-bg)] text-[color:var(--warn-text)]',
  violet:'bg-[color:var(--tint)] text-[color:var(--accent)]',
};

export function Chip({ tone = 'sand', children, title, className }) {
  return (
    <span title={title} className={cx('inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold', CHIP_TONES[tone], className)}>
      {children}
    </span>
  );
}

export function Avatar({ person, size = 32 }) {
  const name = fullName(person);
  if (person?.profileImageUrl) {
    return <img src={person.profileImageUrl} alt="" className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background: avatarColor(person?.firstName ?? name) }}
    >
      {getInitials(name)}
    </span>
  );
}

export function AvatarStack({ members = [], max = 4, size = 26 }) {
  const list = Array.isArray(members) ? members : [];
  const visible = list.slice(0, max);
  const extra = list.length - visible.length;
  if (!list.length) return null;
  return (
    <div className="flex items-center">
      {visible.map((m, i) => (
        <span key={m.id ?? i} title={fullName(m)} className={cx('rounded-full ring-2 ring-[color:var(--surface)]', i > 0 && '-ml-2')}>
          <Avatar person={m} size={size} />
        </span>
      ))}
      {extra > 0 && (
        <span
          className="-ml-2 inline-flex items-center justify-center rounded-full bg-sand-100 px-1.5 text-[10px] font-bold text-sand-600 ring-2 ring-[color:var(--surface)]"
          style={{ height: size, minWidth: size }}
        >
          +{extra}
        </span>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, message, children }) {
  return (
    <div className="ph-fade flex flex-col items-center px-6 py-12 text-center">
      {Icon && (
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ink-50 text-ink">
          <Icon size={26} />
        </span>
      )}
      <h4 className="m-0 mt-4 text-base font-semibold text-sand-900">{title}</h4>
      {message && <p className="m-0 mt-1 max-w-sm text-sm text-sand-600">{message}</p>}
      {children && <div className="mt-5 flex flex-wrap justify-center gap-2">{children}</div>}
    </div>
  );
}

export function SkeletonRows({ rows = 4, height = 'h-14' }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }, (_, i) => <div key={i} className={cx('animate-pulse rounded-xl bg-sand-50', height)} />)}
    </div>
  );
}

export function Notice({ tone = 'info', children, className }) {
  const tones = {
    info:  'bg-ink-50 text-ink',
    warn:  'bg-[color:var(--warn-bg)] text-[color:var(--warn-text)]',
    error: 'bg-[color:var(--bad-bg)] text-[color:var(--bad-text)]',
  };
  const Icon = tone === 'info' ? Info : AlertTriangle;
  return (
    <div className={cx('flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-[13px] leading-relaxed', tones[tone], className)}>
      <Icon size={15} className="mt-0.5 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Layout pieces
// ─────────────────────────────────────────────────────────────
export function SectionHeader({ title, description, action, leading }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3 border-b border-sand-200 pb-5">
      <div className="flex min-w-0 items-start gap-3">
        {leading}
        <div className="min-w-0">
          <h2 className="m-0 text-xl font-semibold tracking-tight text-sand-900">{title}</h2>
          {description && <p className="m-0 mt-1 max-w-xl text-sm leading-relaxed text-sand-600">{description}</p>}
        </div>
      </div>
      {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
    </div>
  );
}

// One setting: label and hint on the left, control on the right. Stacks on small screens.
export function SettingRow({ label, hint, children }) {
  return (
    <div className="flex flex-col gap-3 border-b border-sand-100 py-5 first:pt-0 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
      <div className="min-w-0 sm:max-w-md">
        <div className="text-sm font-semibold text-sand-900">{label}</div>
        {hint && <p className="m-0 mt-1 text-[13px] leading-relaxed text-sand-600">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

// Sticky bar at the bottom of a settings form
export function SaveBar({ onSave, onDiscard, saving, dirty }) {
  return (
    <div
      className={cx(
        'sticky bottom-3 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3 transition-all',
        dirty ? 'bg-[color:var(--tip-bg)] text-[color:var(--tip-text)] shadow-xl' : 'bg-sand-50 text-sand-600',
      )}
    >
      <span className="flex items-center gap-2 text-[13px]">
        {dirty
          ? <><span className="h-2 w-2 animate-pulse rounded-full bg-amber-400" /> You have unsaved changes</>
          : <><Check size={14} className="text-[color:var(--ok-text)]" /> Everything is saved</>}
      </span>
      <div className="flex gap-2">
        {dirty && onDiscard && (
          <button
            type="button"
            onClick={onDiscard}
            disabled={saving}
            className="h-9 rounded-lg px-3 text-[13px] font-medium text-[color:var(--tip-text)] opacity-80 transition-opacity hover:opacity-100 disabled:opacity-50"
          >
            Discard
          </button>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className={cx(
            'inline-flex h-9 items-center gap-2 rounded-lg px-4 text-[13px] font-semibold transition-all active:scale-[0.98] disabled:cursor-not-allowed',
            dirty ? 'bg-[color:var(--surface)] text-sand-900 hover:bg-sand-100' : 'bg-sand-200 text-sand-500',
          )}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Form controls
// ─────────────────────────────────────────────────────────────
export const selectClass = 'h-10 w-full rounded-xl border border-sand-200 bg-[color:var(--surface)] px-3 text-sm text-sand-900 transition focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/15';
export const inputClass  = 'h-11 w-full rounded-xl border border-sand-200 bg-[color:var(--surface)] px-3.5 text-sm text-sand-900 placeholder:text-sand-500 transition focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/15';
export const textareaClass = 'w-full resize-y rounded-xl border border-sand-200 bg-[color:var(--surface)] px-3.5 py-2.5 text-sm leading-relaxed text-sand-900 placeholder:text-sand-500 transition focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/15';
export const labelClass  = 'mb-1.5 block text-[13px] font-semibold text-sand-700';

export function Toggle({ checked, onChange, disabled, label }) {
  return (
    <span className="inline-flex items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={label || 'Toggle'}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:opacity-50',
          checked ? 'bg-ink' : 'bg-sand-300',
        )}
      >
        <span className={cx('absolute top-0.5 h-5 w-5 rounded-full bg-[color:var(--surface)] shadow transition-all', checked ? 'left-[22px]' : 'left-0.5')} />
      </button>
      {label && <span className={cx('text-sm font-medium', checked ? 'text-sand-900' : 'text-sand-600')}>{label}</span>}
    </span>
  );
}

export function Segmented({ options, value, onChange, className, size = 'sm' }) {
  return (
    <div className={cx('inline-flex rounded-xl bg-sand-100 p-1', className)} role="tablist">
      {options.map(opt => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="tab"
            aria-selected={active}
            title={opt.title}
            onClick={() => onChange(opt.value)}
            className={cx(
              'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-all',
              size === 'md' ? 'h-9 flex-1 px-4 text-sm' : 'h-8 px-3 text-[13px]',
              active ? 'bg-[color:var(--surface)] text-sand-900 shadow-sm' : 'text-sand-600 hover:text-sand-900',
            )}
          >
            {opt.icon && <opt.icon size={14} />}
            {opt.label}
            {opt.count != null && (
              <span className={cx('rounded-full px-1.5 text-[11px] font-semibold', active ? 'bg-ink-50 text-ink' : 'bg-sand-200 text-sand-600')}>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, autoFocus, onKeyDown, className }) {
  return (
    <div className={cx('relative', className)}>
      <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sand-500" />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="h-10 w-full rounded-xl border border-sand-200 bg-[color:var(--surface)] pl-9 pr-9 text-sm text-sand-900 placeholder:text-sand-500 transition focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/15"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-sand-500 hover:bg-sand-100 hover:text-sand-900"
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}

export function NumberInput({ value, onChange, min = 0, max = 999, unit = '' }) {
  const clamp = n => Math.min(max, Math.max(min, Number.isFinite(n) ? n : min));
  const num = Number(value);
  const stepClass = 'flex h-full w-10 items-center justify-center text-sand-700 transition-colors hover:bg-sand-100 disabled:cursor-not-allowed disabled:text-sand-300 disabled:hover:bg-transparent';
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="inline-flex h-10 items-stretch overflow-hidden rounded-xl border border-sand-200 bg-[color:var(--surface)] focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/15">
        <button type="button" aria-label="Decrease" disabled={num <= min} onClick={() => onChange(clamp(num - 1))} className={stepClass}>
          <Minus size={15} />
        </button>
        <input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          onBlur={e => onChange(clamp(Number(e.target.value)))}
          className="ph-num w-14 border-x border-sand-200 bg-[color:var(--surface)] text-center text-[15px] font-semibold text-sand-900 outline-none"
        />
        <button type="button" aria-label="Increase" disabled={num >= max} onClick={() => onChange(clamp(num + 1))} className={stepClass}>
          <Plus size={15} />
        </button>
      </div>
      {unit && <span className="text-sm text-sand-600">{unit}</span>}
    </div>
  );
}

// Custom select with icons and descriptions; closes on outside click / Escape
export function Dropdown({ options, value, onChange, placeholder = 'Select…', className }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find(o => o.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div ref={ref} className={cx('relative', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={cx(
          'flex h-10 w-full items-center justify-between gap-2 rounded-xl border bg-[color:var(--surface)] px-3 text-sm transition',
          open ? 'border-ink ring-2 ring-ink/15' : 'border-sand-200 hover:border-sand-300',
        )}
      >
        <span className={cx('flex min-w-0 items-center gap-2 truncate', selected ? 'text-sand-900' : 'text-sand-500')}>
          {selected?.icon && <selected.icon size={15} style={{ color: selected.color }} className="shrink-0" />}
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={15} className={cx('shrink-0 text-sand-500 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div role="listbox" className="ph-pop absolute left-0 top-full z-50 mt-1.5 max-h-72 w-full min-w-max overflow-y-auto rounded-xl border border-sand-200 bg-[color:var(--surface)] p-1 shadow-xl">
          {options.map(opt => {
            const on = opt.value === value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={cx('flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors', on ? 'bg-ink-50' : 'hover:bg-sand-100')}
              >
                {opt.icon && <opt.icon size={15} style={{ color: opt.color }} className="shrink-0" />}
                <span className="min-w-0">
                  <span className={cx('block', on ? 'font-semibold text-ink' : 'font-medium text-sand-900')}>{opt.label}</span>
                  {opt.desc && <span className="block text-xs text-sand-600">{opt.desc}</span>}
                </span>
                {on && <Check size={15} className="ml-auto shrink-0 text-ink" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Action menu that closes on outside click / Escape
export function Menu({ trigger, items, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      {trigger({ open, toggle: () => setOpen(o => !o) })}
      {open && (
        <div
          role="menu"
          className={cx('ph-pop absolute top-full z-30 mt-2 min-w-[230px] rounded-xl border border-sand-200 bg-[color:var(--surface)] p-1 shadow-xl', align === 'right' ? 'right-0' : 'left-0')}
        >
          {items.filter(Boolean).map(item => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => { setOpen(false); item.onClick(); }}
              className={cx(
                'flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                item.tone === 'danger' ? 'text-[color:var(--bad-text)] hover:bg-[color:var(--bad-bg)]' : 'text-sand-900 hover:bg-sand-100',
              )}
            >
              {item.icon && <item.icon size={15} className="mt-0.5 shrink-0" />}
              <span className="min-w-0">
                <span className="block font-medium">{item.label}</span>
                {item.hint && <span className="block text-xs font-normal text-sand-600">{item.hint}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Modal (stack-aware: Escape closes only the top one)
// ─────────────────────────────────────────────────────────────
const modalStack = [];

export function Modal({ onClose, children, className = 'max-w-lg', z = 200, labelledBy }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const idRef = useRef(Symbol('modal'));

  useEffect(() => {
    const id = idRef.current;
    modalStack.push(id);
    const onKey = e => {
      if (e.key === 'Escape' && modalStack[modalStack.length - 1] === id) closeRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      const i = modalStack.indexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Rendered on <body>: inside the (animated) settings panel, "fixed" would be
  // relative to the panel, so the overlay only covered the panel and tall
  // dialogs were cropped at the top of the screen
  return createPortal(
    <div
      className="ph-fade fixed inset-0 flex items-end justify-center bg-[color:var(--overlay)] backdrop-blur-[2px] sm:items-center sm:p-4"
      style={{ zIndex: z }}
      onMouseDown={e => { if (e.target === e.currentTarget) closeRef.current?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={cx('ph-pop flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-[color:var(--surface)] shadow-2xl sm:rounded-2xl', className)}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function ModalHeader({ id, title, subtitle, onClose, leading }) {
  return (
    <div className="flex items-start gap-3 px-5 pb-2 pt-5 sm:px-6">
      {leading}
      <div className="min-w-0 flex-1">
        <h3 id={id} className="m-0 text-lg font-semibold tracking-tight text-sand-900">{title}</h3>
        {subtitle && <div className="m-0 mt-0.5 text-[13px] text-sand-600">{subtitle}</div>}
      </div>
      {onClose && <IconButton label="Close" onClick={onClose}><X size={17} /></IconButton>}
    </div>
  );
}

export function ModalBody({ children, className }) {
  return <div className={cx('min-h-0 flex-1 overflow-y-auto px-5 pb-2 pt-3 sm:px-6', className)}>{children}</div>;
}

export function ModalFooter({ children, className }) {
  return <div className={cx('mt-2 flex items-center gap-2 border-t border-sand-100 px-5 py-4 sm:px-6', className)}>{children}</div>;
}

export function ModalIcon({ icon: Icon, tone = 'ink' }) {
  return (
    <span className={cx('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', tone === 'danger' ? 'bg-[color:var(--bad-bg)] text-[color:var(--bad-text)]' : 'bg-ink-50 text-ink')}>
      <Icon size={19} />
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Confirm dialog (promise-based replacement for window.confirm)
//   const confirm = useConfirm();
//   if (!(await confirm({ title, message, confirmLabel, tone: 'danger' }))) return;
// ─────────────────────────────────────────────────────────────
const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  // Fallback so components still work outside a provider
  return ctx || (opts => Promise.resolve(window.confirm(opts?.title || 'Are you sure?')));
}

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);

  const confirm = useCallback(opts => new Promise(resolve => setDialog({ ...opts, resolve })), []);
  const finish = value => {
    dialog?.resolve(value);
    setDialog(null);
  };
  const danger = dialog?.tone === 'danger';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <Modal onClose={() => finish(false)} className="max-w-md" z={900} labelledBy="ui-confirm-title">
          <div className="px-6 pb-6 pt-6">
            <div className="flex gap-4">
              <span className={cx('flex h-11 w-11 shrink-0 items-center justify-center rounded-full', danger ? 'bg-[color:var(--bad-bg)] text-[color:var(--bad-text)]' : 'bg-ink-50 text-ink')}>
                {danger ? <AlertTriangle size={20} /> : <Info size={20} />}
              </span>
              <div className="min-w-0">
                <h3 id="ui-confirm-title" className="m-0 text-base font-semibold text-sand-900">{dialog.title}</h3>
                {dialog.message && <p className="m-0 mt-1 text-sm leading-relaxed text-sand-600">{dialog.message}</p>}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button onClick={() => finish(false)}>{dialog.cancelLabel || 'Cancel'}</Button>
              <Button variant={danger ? 'danger' : 'primary'} onClick={() => finish(true)} autoFocus>
                {dialog.confirmLabel || 'Confirm'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}